import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Tab,
  Tabs,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShowChart as ForecastIcon,
  AttachMoney as PricingIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as ApproveIcon,
  PlayArrow as ApplyIcon,
  Refresh as RefreshIcon,
  Download as ExportIcon,
  Warning as AlertIcon,
  Info as InfoIcon,
  CalendarMonth as CalendarIcon,
  Assessment as AssessmentIcon,
  Speed as OptimizeIcon,
  Lightbulb as InsightIcon,
  Timeline as TimelineIcon,
  PriceCheck as PriceCheckIcon,
  Hotel as OccupancyIcon,
  MonetizationOn as RevenueIcon,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import dayjs from 'dayjs';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`revenue-tabpanel-${index}`}
      aria-labelledby={`revenue-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const RevenueOptimizationPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [locations, setLocations] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [optimizations, setOptimizations] = useState<any[]>([]);
  const [selectedOptimization, setSelectedOptimization] = useState<any>(null);
  
  // Forecast state
  const [forecast, setForecast] = useState<any>(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  
  // Pricing state
  const [pricingRecommendations, setPricingRecommendations] = useState<any>(null);
  const [pricingRules, setPricingRules] = useState<any[]>([]);
  
  // Analysis state
  const [revenueAnalysis, setRevenueAnalysis] = useState<any>(null);
  const [alerts, setAlerts] = useState<any[]>([]);
  
  // Dialog states
  const [forecastDialog, setForecastDialog] = useState(false);
  const [pricingDialog, setPricingDialog] = useState(false);
  const [analysisDialog, setAnalysisDialog] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  
  // Form states
  const [forecastForm, setForecastForm] = useState({
    startDate: dayjs(),
    endDate: dayjs().add(90, 'day'),
    includeSeasonality: true,
    includeEvents: true,
    includeWeather: false
  });
  
  const [pricingForm, setPricingForm] = useState({
    startDate: dayjs(),
    endDate: dayjs().add(30, 'day'),
    targetOccupancy: 85,
    priceElasticity: -1.5
  });
  
  const [analysisForm, setAnalysisForm] = useState({
    startDate: dayjs().subtract(30, 'day'),
    endDate: dayjs()
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchOptimizations();
      fetchLatestData();
      fetchAlerts();
    }
  }, [selectedLocation]);

  const fetchLocations = async () => {
    try {
      const response = await api.get(
        `/locations`      );
      const locationData = response.data.locations || response.data || [];
      setLocations(Array.isArray(locationData) ? locationData : []);
      if (locationData.length > 0) {
        setSelectedLocation(locationData[0]._id);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchOptimizations = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/revenue-optimization/location/${selectedLocation}`      );
      const optimizationData = response.data || [];
      setOptimizations(Array.isArray(optimizationData) ? optimizationData : []);
    } catch (error) {
      console.error('Error fetching optimizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestData = async () => {
    try {
      // Fetch latest forecast
      const forecastResponse = await api.get(
        `/revenue-optimization/location/${selectedLocation}/latest-forecast`      );
      setForecast(forecastResponse.data);
      
      // Fetch active pricing rules
      const rulesResponse = await api.get(
        `/revenue-optimization/location/${selectedLocation}/pricing-rules`      );
      const rulesData = rulesResponse.data || [];
      setPricingRules(Array.isArray(rulesData) ? rulesData : []);
    } catch (error) {
      console.error('Error fetching latest data:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await api.get(
        `/revenue-optimization/location/${selectedLocation}/alerts`      );
      const alertsData = response.data || [];
      setAlerts(Array.isArray(alertsData) ? alertsData : []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const handleGenerateForecast = async () => {
    if (!selectedLocation) {
      console.error('No location selected');
      return;
    }
    
    try {
      setForecastLoading(true);
      const response = await api.post(
        `/revenue-optimization/forecast`,
        {
          locationId: selectedLocation,
          startDate: forecastForm.startDate.toISOString(),
          endDate: forecastForm.endDate.toISOString(),
          includeSeasonality: forecastForm.includeSeasonality,
          includeEvents: forecastForm.includeEvents,
          includeWeather: forecastForm.includeWeather
        }
      );
      
      setForecast(response.data);
      setForecastDialog(false);
      fetchOptimizations();
    } catch (error) {
      console.error('Error generating forecast:', error);
    } finally {
      setForecastLoading(false);
    }
  };

  const handleGeneratePricing = async () => {
    try {
      setLoading(true);
      const response = await api.post(
        `/revenue-optimization/pricing`,
        {
          locationId: selectedLocation,
          startDate: pricingForm.startDate.toISOString(),
          endDate: pricingForm.endDate.toISOString(),
          targetOccupancy: pricingForm.targetOccupancy,
          priceElasticity: pricingForm.priceElasticity
        }      );
      
      setPricingRecommendations(response.data);
      setPricingDialog(false);
      fetchOptimizations();
    } catch (error) {
      console.error('Error generating pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeRevenue = async () => {
    try {
      setLoading(true);
      const response = await api.post(
        `/revenue-optimization/analyze`,
        {
          locationId: selectedLocation,
          startDate: analysisForm.startDate.toISOString(),
          endDate: analysisForm.endDate.toISOString()
        }      );
      
      setRevenueAnalysis(response.data);
      setAnalysisDialog(false);
      fetchOptimizations();
    } catch (error) {
      console.error('Error analyzing revenue:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveOptimization = async (optimizationId: string) => {
    try {
      await api.post(
        `/revenue-optimization/${optimizationId}/approve`,
        {}      );
      
      fetchOptimizations();
      if (selectedOptimization?._id === optimizationId) {
        fetchOptimizationDetails(optimizationId);
      }
    } catch (error) {
      console.error('Error approving optimization:', error);
    }
  };

  const handleApplyPricing = async (optimizationId: string) => {
    if (window.confirm('Are you sure you want to apply these pricing recommendations? This will update all site prices.')) {
      try {
        await api.post(
          `/revenue-optimization/${optimizationId}/apply`,
          {}
        );
        
        alert('Pricing recommendations applied successfully!');
        fetchOptimizations();
      } catch (error) {
        console.error('Error applying pricing:', error);
        alert('Error applying pricing recommendations');
      }
    }
  };

  const fetchOptimizationDetails = async (id: string) => {
    try {
      const response = await api.get(
        `/revenue-optimization/${id}`      );
      setSelectedOptimization(response.data);
      setDetailsDialog(true);
    } catch (error) {
      console.error('Error fetching optimization details:', error);
    }
  };

  const getOptimizationIcon = (type: string) => {
    switch (type) {
      case 'forecast':
        return <ForecastIcon />;
      case 'pricing_recommendation':
        return <PricingIcon />;
      case 'analysis':
        return <AnalyticsIcon />;
      default:
        return <AssessmentIcon />;
    }
  };

  const getAlertIcon = (severity: string) => {
    switch (severity) {
      case 'high':
        return <AlertIcon color="error" />;
      case 'medium':
        return <AlertIcon color="warning" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const renderMetricCard = (title: string, value: any, change?: number, icon?: React.ReactNode) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4">
          {value}
        </Typography>
        {change !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            {change > 0 ? (
              <ArrowUpward color="success" />
            ) : (
              <ArrowDownward color="error" />
            )}
            <Typography
              variant="body2"
              color={change > 0 ? 'success.main' : 'error.main'}
            >
              {Math.abs(change).toFixed(1)}% {change > 0 ? 'increase' : 'decrease'}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderForecastChart = () => {
    if (!forecast?.forecast?.occupancy) return null;

    const chartData = forecast.forecast.occupancy.slice(0, 30).map((day: any) => ({
      date: dayjs(day.date).format('MM/DD'),
      predicted: day.predicted,
      lower: day.confidence.lower,
      upper: day.confidence.upper
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <ChartTooltip />
          <Legend />
          <Area
            type="monotone"
            dataKey="upper"
            stackId="1"
            stroke="#8884d8"
            fill="#8884d8"
            fillOpacity={0.2}
            name="Upper Bound"
          />
          <Area
            type="monotone"
            dataKey="predicted"
            stackId="2"
            stroke="#82ca9d"
            fill="#82ca9d"
            fillOpacity={0.6}
            name="Predicted"
          />
          <Area
            type="monotone"
            dataKey="lower"
            stackId="3"
            stroke="#8884d8"
            fill="#8884d8"
            fillOpacity={0.2}
            name="Lower Bound"
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  const renderRevenueChart = () => {
    if (!forecast?.forecast?.revenue) return null;

    const chartData = forecast.forecast.revenue.slice(0, 30).map((day: any) => ({
      date: dayjs(day.date).format('MM/DD'),
      sites: day.breakdown.sites,
      amenities: day.breakdown.amenities,
      pos: day.breakdown.pos
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <ChartTooltip />
          <Legend />
          <Bar dataKey="sites" stackId="a" fill="#8884d8" name="Site Revenue" />
          <Bar dataKey="amenities" stackId="a" fill="#82ca9d" name="Amenity Revenue" />
          <Bar dataKey="pos" stackId="a" fill="#ffc658" name="POS Revenue" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Revenue Optimization & Forecasting
          </Typography>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Location</InputLabel>
              <Select
                value={selectedLocation}
                label="Location"
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                {locations && locations.map((location) => (
                  <MenuItem key={location._id} value={location._id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchOptimizations();
                fetchLatestData();
                fetchAlerts();
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Box>
        <Typography variant="body1" color="text.secondary">
          AI-powered revenue management and demand forecasting
        </Typography>
      </Box>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {alerts.map((alert, index) => (
            <Alert
              key={index}
              severity={alert.severity === 'high' ? 'error' : alert.severity === 'medium' ? 'warning' : 'info'}
              icon={getAlertIcon(alert.severity)}
              sx={{ mb: 1 }}
              action={
                <Button size="small" color="inherit">
                  View Details
                </Button>
              }
            >
              <strong>{alert.title}</strong> - {alert.description}
            </Alert>
          ))}
        </Box>
      )}

      {/* Key Metrics */}
      {forecast && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            {renderMetricCard(
              'Forecasted Revenue (30d)',
              formatCurrency(
                forecast.forecast.revenue
                  .slice(0, 30)
                  .reduce((sum: number, day: any) => sum + day.predicted, 0)
              ),
              15.2,
              <RevenueIcon color="primary" />
            )}
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            {renderMetricCard(
              'Avg Occupancy (30d)',
              formatPercentage(
                forecast.forecast.occupancy
                  .slice(0, 30)
                  .reduce((sum: number, day: any) => sum + day.predicted, 0) / 30
              ),
              8.5,
              <OccupancyIcon color="success" />
            )}
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            {renderMetricCard(
              'Forecast Accuracy',
              formatPercentage(forecast.forecast.accuracy?.r2 * 100 || 82),
              undefined,
              <TimelineIcon color="info" />
            )}
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            {renderMetricCard(
              'Active Pricing Rules',
              pricingRules.length,
              undefined,
              <PriceCheckIcon color="warning" />
            )}
          </Grid>
        </Grid>
      )}

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Forecast" icon={<ForecastIcon />} iconPosition="start" />
          <Tab label="Pricing" icon={<PricingIcon />} iconPosition="start" />
          <Tab label="Analysis" icon={<AnalyticsIcon />} iconPosition="start" />
          <Tab label="History" icon={<TimelineIcon />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Forecast Tab */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Revenue & Occupancy Forecast</Typography>
            <Button
              variant="contained"
              startIcon={<ForecastIcon />}
              onClick={() => setForecastDialog(true)}
            >
              Generate New Forecast
            </Button>
          </Box>

          {forecast ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Occupancy Forecast (30 days)
                  </Typography>
                  {renderForecastChart()}
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Revenue Forecast by Source (30 days)
                  </Typography>
                  {renderRevenueChart()}
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Demand Patterns
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary="Peak Days"
                            secondary="Friday, Saturday"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Average Stay"
                            secondary="2.8 nights"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Booking Window"
                            secondary="14 days advance"
                          />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Typography variant="body2" color="text.secondary">
                        Forecast factors include seasonality ({forecast.forecast.occupancy[0]?.factors.seasonality.toFixed(2)}x),
                        day of week ({forecast.forecast.occupancy[0]?.factors.dayOfWeek.toFixed(2)}x),
                        and historical patterns.
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No forecast available
              </Typography>
              <Button
                variant="contained"
                startIcon={<ForecastIcon />}
                onClick={() => setForecastDialog(true)}
                sx={{ mt: 2 }}
              >
                Generate Forecast
              </Button>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Pricing Tab */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Dynamic Pricing Recommendations</Typography>
            <Button
              variant="contained"
              startIcon={<PricingIcon />}
              onClick={() => setPricingDialog(true)}
            >
              Generate Pricing
            </Button>
          </Box>

          {pricingRules.length > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Active Pricing Rules
              </Typography>
              <List>
                {pricingRules && pricingRules.map((rule, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <PriceCheckIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={rule.name}
                      secondary={`${rule.condition.type} ${rule.condition.operator} ${rule.condition.value} → ${rule.action.type} ${rule.action.value}`}
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        label={rule.enabled ? 'Active' : 'Inactive'}
                        color={rule.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            Pricing recommendations are based on demand forecasting, competitor analysis, and revenue optimization algorithms
          </Alert>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Analysis Tab */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Revenue Performance Analysis</Typography>
            <Button
              variant="contained"
              startIcon={<AnalyticsIcon />}
              onClick={() => setAnalysisDialog(true)}
            >
              Run Analysis
            </Button>
          </Box>

          {revenueAnalysis && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Revenue Drivers
                  </Typography>
                  <List>
                    {revenueAnalysis.analysis?.revenueDrivers?.map((driver: any, index: number) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={driver.driver}
                          secondary={`${driver.impact}% of revenue - ${driver.trend}`}
                        />
                        <ListItemSecondaryAction>
                          {driver.trend === 'growing' ? (
                            <TrendingUpIcon color="success" />
                          ) : driver.trend === 'declining' ? (
                            <TrendingDownIcon color="error" />
                          ) : null}
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Opportunities
                  </Typography>
                  <List>
                    {revenueAnalysis.analysis?.opportunities?.map((opp: any, index: number) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <InsightIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={opp.description}
                          secondary={`Potential: ${formatCurrency(opp.potentialRevenue)} - ${opp.priority} priority`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {/* History Tab */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date Range</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : !optimizations || optimizations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No optimization history
                    </TableCell>
                  </TableRow>
                ) : (
                  optimizations.map((opt) => (
                    <TableRow key={opt._id}>
                      <TableCell>
                        {dayjs(opt.createdAt).format('MMM D, YYYY')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getOptimizationIcon(opt.type)}
                          label={opt.type.replace('_', ' ')}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {dayjs(opt.dateRange.startDate).format('MM/DD')} - {dayjs(opt.dateRange.endDate).format('MM/DD')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={opt.status}
                          color={opt.status === 'completed' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {opt.metadata?.createdBy?.firstName} {opt.metadata?.createdBy?.lastName}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => fetchOptimizationDetails(opt._id)}
                            >
                              <InfoIcon />
                            </IconButton>
                          </Tooltip>
                          
                          {opt.type === 'pricing_recommendation' && !opt.metadata?.approved && (
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleApproveOptimization(opt._id)}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {opt.type === 'pricing_recommendation' && opt.metadata?.approved && !opt.metadata?.applied && (
                            <Tooltip title="Apply Pricing">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleApplyPricing(opt._id)}
                              >
                                <ApplyIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          <Tooltip title="Export">
                            <IconButton size="small">
                              <ExportIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Generate Forecast Dialog */}
      <Dialog open={forecastDialog} onClose={() => setForecastDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Revenue Forecast</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Start Date"
                value={forecastForm.startDate}
                onChange={(value) => setForecastForm({ ...forecastForm, startDate: value || dayjs() })}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="End Date"
                value={forecastForm.endDate}
                onChange={(value) => setForecastForm({ ...forecastForm, endDate: value || dayjs() })}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Include Factors
              </Typography>
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={forecastForm.includeSeasonality}
                      onChange={(e) => setForecastForm({ ...forecastForm, includeSeasonality: e.target.checked })}
                    />
                  }
                  label="Seasonality"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={forecastForm.includeEvents}
                      onChange={(e) => setForecastForm({ ...forecastForm, includeEvents: e.target.checked })}
                    />
                  }
                  label="Local Events"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={forecastForm.includeWeather}
                      onChange={(e) => setForecastForm({ ...forecastForm, includeWeather: e.target.checked })}
                    />
                  }
                  label="Weather Patterns"
                />
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForecastDialog(false)}>Cancel</Button>
          <Button
            onClick={handleGenerateForecast}
            variant="contained"
            disabled={forecastLoading}
            startIcon={forecastLoading ? <CircularProgress size={20} /> : <ForecastIcon />}
          >
            Generate Forecast
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Pricing Dialog */}
      <Dialog open={pricingDialog} onClose={() => setPricingDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Pricing Recommendations</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Start Date"
                value={pricingForm.startDate}
                onChange={(value) => setPricingForm({ ...pricingForm, startDate: value || dayjs() })}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="End Date"
                value={pricingForm.endDate}
                onChange={(value) => setPricingForm({ ...pricingForm, endDate: value || dayjs() })}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Target Occupancy %"
                type="number"
                value={pricingForm.targetOccupancy}
                onChange={(e) => setPricingForm({ ...pricingForm, targetOccupancy: parseFloat(e.target.value) })}
                InputProps={{ inputProps: { min: 0, max: 100 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                The system will analyze demand patterns and competitor pricing to recommend optimal rates
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPricingDialog(false)}>Cancel</Button>
          <Button
            onClick={handleGeneratePricing}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PricingIcon />}
          >
            Generate Recommendations
          </Button>
        </DialogActions>
      </Dialog>

      {/* Analyze Revenue Dialog */}
      <Dialog open={analysisDialog} onClose={() => setAnalysisDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Analyze Revenue Performance</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Start Date"
                value={analysisForm.startDate}
                onChange={(value) => setAnalysisForm({ ...analysisForm, startDate: value || dayjs() })}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="End Date"
                value={analysisForm.endDate}
                onChange={(value) => setAnalysisForm({ ...analysisForm, endDate: value || dayjs() })}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                Analysis will include performance metrics, demand patterns, revenue drivers, and improvement opportunities
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalysisDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAnalyzeRevenue}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <AnalyticsIcon />}
          >
            Run Analysis
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RevenueOptimizationPage;